import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import {
  MapPin, Phone, MessageSquare, Sparkles, Check, ArrowRight, ArrowUpRight,
  Clock, TrendingUp, ShieldCheck, Plus, Minus, Menu, X as XIcon, Calendar,
} from "lucide-react";

// ───────────────────────────────────────────────────────────────────────────
// Heetch Pro — banking-grade dark, premium violet accent, massive typography.
// Stark backgrounds, sharp dividers, zero decorative noise.
// ───────────────────────────────────────────────────────────────────────────

const CHANNELS = [
  {
    id: "terrain",
    icon: MapPin,
    label: "Road Maps",
    title: "Road Maps : prospection terrain augmentée",
    desc: "Vos prospects B2B s'affichent en temps réel sur la carte. Avant chaque visite, vous recevez un brief commercial complet. Après, la fiche lead est créée automatiquement.",
    cta: "Démarrer une tournée terrain",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "phone",
    icon: Phone,
    label: "Téléphone",
    title: "Appels à froid : convertissez sans improviser",
    desc: "Script contextuel, qualification guidée, réponses aux objections : tout s'affiche en direct pendant l'appel pour augmenter votre taux de prise de rendez-vous.",
    cta: "Préparer un appel",
    image: "https://images.unsplash.com/photo-1553484771-047a44eee27b?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "social",
    icon: MessageSquare,
    label: "Réseaux",
    title: "Prospection LinkedIn et WhatsApp : un message par lead",
    desc: "LinkedIn, WhatsApp, Instagram : un message commercial personnalisé par prospect, prêt à envoyer en cinq secondes. Multipliez vos taux de réponse par trois.",
    cta: "Générer un message commercial",
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=1600&q=80",
  },
];

const FAQS = [
  { q: "Combien de temps pour être opérationnel sur Prosperr ?", a: "Cinq minutes de configuration. Prosperr s'adapte à votre offre, à votre zone géographique et à votre client idéal. Les premières recommandations arrivent dès la fin de l'analyse." },
  { q: "Prosperr remplace-t-il mes commerciaux ?", a: "Non, Prosperr augmente vos commerciaux. Le prospect ne voit jamais l'assistant. Vos équipes gardent la main et bénéficient d'un copilote commercial dans leur poche." },
  { q: "Mes données B2B sont-elles en sécurité ?", a: "Hébergement européen, chiffrement TLS 1.3, aucun tracker publicitaire. Vos données n'alimentent aucun modèle externe et restent votre propriété." },
  { q: "Puis-je importer ma base existante de prospects ?", a: "Oui. Importez votre fichier Excel, CSV ou PDF. Prosperr dédoublonne, attribue un score selon votre client idéal et recommande le meilleur canal pour chaque lead." },
];

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full text-left border-b border-[var(--border-soft)] py-6 group"
      data-testid={`faq-${q.slice(0, 10)}`}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="font-display text-lg sm:text-xl font-medium text-[var(--text)] leading-tight">{q}</div>
        <div className="mt-1 shrink-0 w-8 h-8 rounded-full border border-[var(--border)] grid place-items-center group-hover:border-emerald-500 group-hover:text-emerald-300 transition-colors text-zinc-400">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </div>
      </div>
      {open && <div className="text-sm text-zinc-400 mt-4 leading-relaxed max-w-2xl">{a}</div>}
    </button>
  );
}

function RoiCalculator() {
  const [team, setTeam] = useState(3);
  const [basket, setBasket] = useState(15000);
  const [deals, setDeals] = useState(2);

  const result = useMemo(() => {
    const monthlyDeals = team * deals;
    const baseRevenue = monthlyDeals * basket;
    const withProsperr = Math.round(baseRevenue * 1.18);
    const gain = withProsperr - baseRevenue;
    const hoursSaved = team * 4 * 4;
    return { baseRevenue, withProsperr, gain, hoursSaved };
  }, [team, basket, deals]);

  return (
    <div className="grid md:grid-cols-2 gap-0 bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
      <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[var(--border)]">
        <div className="hp-label mb-3">Simulateur</div>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[var(--text)] leading-tight tracking-tight">
          Combien vous fait gagner Prosperr&nbsp;?
        </h3>
        <div className="mt-8 space-y-6">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-400">Commerciaux</span>
              <span className="font-semibold text-[var(--text)] tabular-nums">{team}</span>
            </div>
            <Slider min={1} max={10} step={1} value={[team]} onValueChange={(v) => setTeam(v[0])} data-testid="roi-team" />
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-400">Panier moyen client</span>
              <span className="font-semibold text-[var(--text)] tabular-nums">{basket.toLocaleString("fr-FR")} €</span>
            </div>
            <Slider min={500} max={100000} step={500} value={[basket]} onValueChange={(v) => setBasket(v[0])} data-testid="roi-basket" />
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-400">Deals par commercial / mois</span>
              <span className="font-semibold text-[var(--text)] tabular-nums">{deals}</span>
            </div>
            <Slider min={1} max={20} step={1} value={[deals]} onValueChange={(v) => setDeals(v[0])} data-testid="roi-deals" />
          </div>
        </div>
      </div>

      <div className="p-8 md:p-10 bg-[var(--surface-alt)] text-white flex flex-col relative overflow-hidden">
        <div className="brand-glow absolute inset-0 pointer-events-none opacity-70" />
        <div className="relative">
          <div className="hp-label">CA mensuel projeté</div>
          <div className="font-display text-5xl sm:text-6xl font-semibold text-white mt-3 tabular-nums leading-none tracking-tight">
            {result.withProsperr.toLocaleString("fr-FR")} €
          </div>
          <div className="mt-6 flex items-center gap-3 border-t border-[var(--border)] pt-6">
            <TrendingUp className="w-5 h-5 text-emerald-400" strokeWidth={1.8} />
            <div className="text-sm">
              <span className="font-semibold text-white tabular-nums">+{result.gain.toLocaleString("fr-FR")} €</span>
              <span className="text-zinc-400"> de gain mensuel estimé</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Clock className="w-5 h-5 text-emerald-400" strokeWidth={1.8} />
            <div className="text-sm text-zinc-400">
              <span className="font-semibold text-white tabular-nums">~{result.hoursSaved}h</span> économisées / mois
            </div>
          </div>
        </div>
        <div className="mt-auto pt-8 relative">
          <Link to="/register">
            <button className="hp-btn hp-btn-primary w-full h-12" data-testid="roi-cta">
              Commencer maintenant
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [activeChannel, setActiveChannel] = useState("terrain");
  const [navOpen, setNavOpen] = useState(false);
  const channel = CHANNELS.find((c) => c.id === activeChannel);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased">
      {/* ───── NAV ───── */}
      <header className="sticky top-0 z-40 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center shadow-glow">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-display font-semibold text-xl tracking-tight text-[var(--text)]">Prosperr</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#produit" className="text-zinc-400 hover:text-white transition-colors">Produit</a>
            <a href="#comment" className="text-zinc-400 hover:text-white transition-colors">Comment ça marche</a>
            <a href="#roi" className="text-zinc-400 hover:text-white transition-colors">Rentabilité</a>
            <Link to="/pricing" className="text-zinc-400 hover:text-white transition-colors">Tarifs</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-block">
              <button data-testid="login-link" className="hp-btn hp-btn-tertiary">
                Se connecter
              </button>
            </Link>
            <Link to="/register">
              <button data-testid="cta-register" className="hp-btn hp-btn-primary">
                Essai gratuit
              </button>
            </Link>
            <button
              onClick={() => setNavOpen(!navOpen)}
              className="md:hidden w-10 h-10 grid place-items-center rounded-lg border border-[var(--border)] text-zinc-400 hover:text-white transition-colors"
              aria-label="Menu"
            >
              {navOpen ? <XIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {navOpen && (
          <div className="md:hidden border-t border-[var(--border)] bg-[var(--bg)]">
            <div className="flex flex-col px-6 py-4 gap-4 text-base font-medium">
              <a href="#produit" onClick={() => setNavOpen(false)} className="text-zinc-300">Produit</a>
              <a href="#comment" onClick={() => setNavOpen(false)} className="text-zinc-300">Comment ça marche</a>
              <a href="#roi" onClick={() => setNavOpen(false)} className="text-zinc-300">Rentabilité</a>
              <Link to="/pricing" onClick={() => setNavOpen(false)} className="text-zinc-300">Tarifs</Link>
              <Link to="/login" onClick={() => setNavOpen(false)} className="text-zinc-300">Se connecter</Link>
            </div>
          </div>
        )}
      </header>

      {/* ───── HERO — split screen ───── */}
      <section className="border-b border-[var(--border)] relative overflow-hidden">
        <div className="brand-glow absolute inset-0 pointer-events-none opacity-60" />
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 relative">
          <div className="px-6 py-16 sm:py-20 lg:py-28 lg:pr-16 flex flex-col justify-center">
            <div className="hp-label mb-4">Assistant commercial B2B en français</div>
            <h1 className="font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl">
              Le copilote commercial<br />
              <span className="text-zinc-500">qui aide vos équipes à vendre plus.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Prosperr accompagne vos commerciaux à chaque visite, appel et message commercial. En temps réel, sans jamais apparaître à vos prospects.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register">
                <button data-testid="cta-trial" className="hp-btn hp-btn-primary h-12 px-6 text-base">
                  Démarrer mon essai gratuit
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/login">
                <button data-testid="cta-demo" className="hp-btn hp-btn-secondary h-12 px-6 text-base">
                  Voir la démonstration
                </button>
              </Link>
              <button
                data-testid="cta-book-calendly"
                onClick={() => {
                  if (typeof window !== "undefined" && window.Calendly?.initPopupWidget) {
                    window.Calendly.initPopupWidget({
                      url: "https://calendly.com/abdelvip",
                      utm: { utmSource: "prosperr-landing" },
                    });
                    return false;
                  }
                  window.open("https://calendly.com/abdelvip", "_blank", "noopener,noreferrer");
                }}
                className="hp-btn hp-btn-tertiary h-12 px-6 text-base"
              >
                <Calendar className="w-4 h-4" />
                Réserver une démo live
              </button>
            </div>
            <div className="mt-8 text-xs text-zinc-500 flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              14 jours d'essai · sans carte bancaire · sans engagement
            </div>
          </div>

          <div className="relative aspect-square lg:aspect-auto lg:min-h-[640px] bg-black overflow-hidden border-l border-[var(--border)]">
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1800&q=85"
              alt="Commercial utilisant Prosperr"
              className="absolute inset-0 w-full h-full object-cover opacity-80"
              loading="eager"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/40 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 hp-card p-6 max-w-md glass">
              <div className="hp-label mb-2">Témoignage client</div>
              <blockquote className="font-display text-base sm:text-lg font-medium text-[var(--text)] leading-snug">
                «&nbsp;Mes trois commerciaux improvisaient. Avec Prosperr, chacun a son copilote commercial dans la poche.&nbsp;»
              </blockquote>
              <div className="mt-4 text-sm text-zinc-400">
                <span className="font-medium text-[var(--text)]">Marc Dubois</span> · Gérant, Rénovation Dubois
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── PRODUIT — channel tabs ───── */}
      <section id="produit" className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="hp-label">Produit</div>
            <h2 className="mt-3 font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              Une plateforme. Trois canaux de prospection. <span className="text-zinc-500">Une expérience fluide.</span>
            </h2>
          </div>

          <div className="mt-12 flex gap-1 border-b border-[var(--border)] overflow-x-auto no-scrollbar">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              const active = c.id === activeChannel;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveChannel(c.id)}
                  data-testid={`tab-${c.id}`}
                  className={`relative flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    active ? "text-[var(--text)]" : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                  {c.label}
                  {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />}
                </button>
              );
            })}
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-[var(--border)] order-2 lg:order-1">
              <img
                key={channel.image}
                src={channel.image}
                alt={channel.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)]/60 to-transparent pointer-events-none" />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[var(--text)] leading-tight tracking-tight">
                {channel.title}
              </h3>
              <p className="mt-6 text-base text-zinc-400 leading-relaxed max-w-lg">{channel.desc}</p>
              <Link to="/register" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 group" data-testid={`channel-cta-${channel.id}`}>
                {channel.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───── COMMENT — 3 steps ───── */}
      <section id="comment" className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-16">
            <div className="hp-label">Comment ça marche</div>
            <h2 className="mt-3 font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              Opérationnel en trois étapes simples.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { n: "01", title: "Configurez votre profil commercial", desc: "Cinq minutes suffisent pour décrire votre offre, votre zone d'intervention et votre client idéal. Prosperr s'adapte à votre activité." },
              { n: "02", title: "Prosperr prépare, vous concluez", desc: "Brief avant chaque visite, script avant chaque appel, message personnalisé pour chaque prospect : tout est préparé en amont par votre assistant." },
              { n: "03", title: "Votre pipeline commercial s'enrichit chaque jour", desc: "Les relances sont priorisées automatiquement. Le rapport hebdomadaire arrive le lundi à 8h. Vous pilotez votre activité commerciale au lieu de compiler des tableurs." },
            ].map((step) => (
              <div key={step.n} className="border-t border-emerald-500 pt-6" data-testid={`step-${step.n}`}>
                <div className="font-display text-sm font-semibold text-emerald-400">{step.n}</div>
                <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold text-[var(--text)] leading-tight">{step.title}</h3>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── ROI ───── */}
      <section id="roi" className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-12">
            <div className="hp-label">Retour sur investissement</div>
            <h2 className="mt-3 font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              Un investissement rentable dès la première semaine d'utilisation.
            </h2>
          </div>
          <RoiCalculator />
        </div>
      </section>

      {/* ───── TÉMOIGNAGES ───── */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-16">
            <div className="hp-label">Ils utilisent Prosperr au quotidien</div>
            <h2 className="mt-3 font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              Plus de 500 PME et indépendants français <span className="text-zinc-500">accélèrent leur prospection avec Prosperr.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { metric: "+40%", label: "de visites terrain", quote: "En trois mois, nos commerciaux réalisent 40 % de visites en plus. Les briefs commerciaux préparés avant chaque visite changent vraiment la donne sur le terrain.", name: "Marc Dubois", role: "Gérant · Rénovation Dubois" },
              { metric: "×3", label: "de conversions LinkedIn", quote: "Nos taux de conversion LinkedIn ont triplé. Prosperr propose un message commercial différent et personnalisé pour chaque prospect — c'est bluffant.", name: "Claire Fontaine", role: "Directrice · Fontaine RH" },
              { metric: "−2h", label: "de reporting hebdomadaire", quote: "Le rapport commercial automatique a remplacé nos deux heures de réunion hebdomadaire. Je pilote l'équipe au lieu de compiler des tableurs.", name: "Pierre Legrand", role: "Directeur Commercial · Legrand & Associés" },
            ].map((t, i) => (
              <div key={i} className="hp-card p-8 flex flex-col min-h-[360px]" data-testid={`testimonial-${i}`}>
                <div className="font-display text-5xl sm:text-6xl font-semibold tracking-tight leading-none text-emerald-400">{t.metric}</div>
                <div className="mt-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">{t.label}</div>
                <blockquote className="mt-auto pt-8 text-sm leading-relaxed text-zinc-300">«&nbsp;{t.quote}&nbsp;»</blockquote>
                <div className="mt-6 pt-5 border-t border-[var(--border-soft)]">
                  <div className="font-medium text-[var(--text)] text-sm">{t.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-3 gap-16">
          <div>
            <div className="hp-label">Questions fréquentes</div>
            <h2 className="mt-3 font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl">
              Tout ce que vous voulez savoir sur Prosperr.
            </h2>
          </div>
          <div className="lg:col-span-2">
            {FAQS.map((f, i) => <Faq key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="relative overflow-hidden">
        <div className="brand-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-28 relative">
          <div className="max-w-3xl">
            <h2 className="font-display font-semibold text-[var(--text)] tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              Donnez un copilote commercial<br />
              <span className="text-zinc-500">à chacun de vos commerciaux.</span>
            </h2>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register">
                <button data-testid="final-cta" className="hp-btn hp-btn-primary h-12 px-6 text-base">
                  Démarrer mon essai gratuit
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/pricing">
                <button className="hp-btn hp-btn-secondary h-12 px-6 text-base">
                  Découvrir les tarifs
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-[1400px] mx-auto px-6 py-14">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center">
                  <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
                </div>
                <span className="font-display font-semibold text-lg text-[var(--text)]">Prosperr</span>
              </div>
              <p className="text-sm text-zinc-500 mt-4 leading-relaxed max-w-xs">
                Le copilote commercial des PME et indépendants français.
              </p>
            </div>
            <div>
              <div className="hp-label mb-4">Produit</div>
              <ul className="space-y-3 text-sm">
                <li><a href="#produit" className="text-zinc-400 hover:text-white transition-colors">Fonctionnalités</a></li>
                <li><a href="#comment" className="text-zinc-400 hover:text-white transition-colors">Comment ça marche</a></li>
                <li><Link to="/pricing" className="text-zinc-400 hover:text-white transition-colors">Tarifs</Link></li>
                <li><Link to="/login" className="text-zinc-400 hover:text-white transition-colors">Se connecter</Link></li>
              </ul>
            </div>
            <div>
              <div className="hp-label mb-4">Entreprise</div>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="text-zinc-400 hover:text-white transition-colors">À propos</a></li>
                <li><a href="#" className="text-zinc-400 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <div className="hp-label mb-4">Légal</div>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-zinc-400 hover:text-white transition-colors">Confidentialité</Link></li>
                <li><Link to="/rgpd" className="text-zinc-400 hover:text-white transition-colors">RGPD</Link></li>
                <li><span className="inline-flex items-center gap-1.5 text-zinc-500"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Hébergement Europe</span></li>
              </ul>
            </div>
          </div>
          <div className="mt-14 pt-6 border-t border-[var(--border-soft)] text-xs text-zinc-600">
            © 2026 Prosperr · Fait en France
          </div>
        </div>
      </footer>
    </div>
  );
}
